import React, { useState } from 'react';
import { Form, Input, Modal, Typography, Button } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import { useCommands } from '@remirror/react';
import { useTranslation } from 'react-i18next';
import { CommandButton } from './CommandButton';
import {
    sanitizeUrl,
    sanitizeToPlainText,
    createUrlValidationRule,
    createTextValidationRule,
    createFormValidationHandler,
    createUrlBlurHandler,
    createTextBlurHandler,
} from '../../../../../../../shared/utils/sanitizationUtils';

export const AddImageButton = () => {
    const { t } = useTranslation();
    const [isModalVisible, setModalVisible] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [form] = Form.useForm();
    const { insertImage } = useCommands();

    const handleButtonClick = () => {
        setModalVisible(true);
    };

    const handleOk = () => {
        form.validateFields()
            .then((values) => {
                // Sanitize the image URL and alt text to prevent XSS attacks
                const sanitizedSrc = values.src ? sanitizeUrl(values.src) : '';
                const sanitizedAlt = values.alt ? sanitizeToPlainText(values.alt) : '';

                // Don't insert image if URL is unsafe
                if (!sanitizedSrc) {
                    console.warn('Image URL blocked for security reasons:', values.src);
                    return;
                }

                form.resetFields();
                setModalVisible(false);
                insertImage({
                    src: sanitizedSrc,
                    alt: sanitizedAlt,
                });
            })
            .catch((info) => {
                console.log('Validate Failed:', info);
            });
    };

    const handleCancel = () => {
        setModalVisible(false);
    };

    return (
        <>
            <CommandButton
                active={false}
                icon={<PictureOutlined />}
                commandName="insertImage"
                onClick={handleButtonClick}
            />
            <Modal
                className="f-modal"
                title={t('Add Image')}
                visible={isModalVisible}
                onCancel={handleCancel}
                centered
                footer={[
                    <Button key="cancel" onClick={handleCancel}>
                        {t('Cancel')}
                    </Button>,
                    <Button key="save" type="primary" onClick={handleOk} disabled={!isFormValid}>
                        {t('Save')}
                    </Button>,
                ]}
            >
                <Form
                    form={form}
                    layout="vertical"
                    colon={false}
                    requiredMark={false}
                    onFieldsChange={createFormValidationHandler(form, setIsFormValid, ['src'])}
                >
                    <Form.Item
                        name="src"
                        label={<Typography.Text strong>{t('Image URL')}</Typography.Text>}
                        rules={[{ required: true, message: t('Image URL is required') }, createUrlValidationRule(t)]}
                    >
                        <Input
                            placeholder="https://www.example.com/image.jpg"
                            autoFocus
                            onBlur={createUrlBlurHandler(form, 'src')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="alt"
                        label={<Typography.Text strong>{t('Alt Text')}</Typography.Text>}
                        rules={[createTextValidationRule(t, 'Alt text', 200)]}
                    >
                        <Input onBlur={createTextBlurHandler(form, 'alt')} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
